"use client"

/* eslint-disable */
import { useState, useEffect } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  TablePagination,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import { formatTime } from "../common/util/formatter"
import ReportFilter from "./components/ReportFilter"
import { prefixString } from "../common/util/stringUtils"
import { useTranslation } from "../common/components/LocalizationProvider"
import PageLayout from "../common/components/PageLayout"
import ReportsMenu from "./components/ReportsMenu"
import usePersistedState from "../common/util/usePersistedState"
import ColumnSelect from "./components/ColumnSelect"
import { useCatch } from "../reactHelper"
import useReportStyles from "./common/useReportStyles"
import TableShimmer from "../common/components/TableShimmer"
import { utils, writeFileXLSX } from "xlsx"
import LogoImage from "../login/LogoImage"
import { nativePostMessage } from "../common/components/NativeInterface"
import { sessionActions, errorsActions } from "../store"

const columnsArray = [
  ["userId", "titleUser"],
  ["eventTime", "positionServerTime"],
  ["deviceId", "sharedDevice"],
  ["attributes", "commandData"],
]
const columnsMap = new Map(columnsArray)

// User filter component similar to actions-report-page
const UserFilter = ({ onChange, users = [], loading = false }) => {
  const [userId, setUserId] = useState("")
  const t = useTranslation()
  const usersMap = useSelector((state) => state.usersMap || {})

  const handleChange = (e) => {
    setUserId(e.target.value)
    onChange(e.target.value)
  }

  const getUserDisplayName = (user) => {
    const nameFromMap = usersMap[user.id]
    if (nameFromMap) return nameFromMap
    if (user.name) return user.name
    return `${user.id}`
  }

  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel id="user-select-label">{t("shared.user") || "User"}</InputLabel>
      <Select
        labelId="user-select-label"
        value={userId}
        onChange={handleChange}
        label={t("shared.user") || "User"}
        displayEmpty
      >
        <MenuItem value="">
          <em>{t("sharedAll") || "All users"}</em>
        </MenuItem>
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id.toString()}>
            {getUserDisplayName(user)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

// Print header component for PDF export
const PrintHeader = ({ from, to, userIdFilter }) => {
  const t = useTranslation()
  const usersMap = useSelector((state) => state.usersMap || {})

  return (
    <div style={{ padding: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print()
                nativePostMessage(`print|${t("reportCommands")}.pdf`)
              }),
            )
          }}
          alt={t("companyLogo")}
        />
        <h1 style={{ fontSize: 24, margin: 0 }}>{t("reportCommands") || "Commands Report"}</h1>
      </div>
      <div style={{ marginTop: 10, fontSize: 14, color: "#555" }}>
        {userIdFilter && (
          <div>
            <strong>{t("shared.user") || "User"}:</strong>{" "}
            {usersMap[userIdFilter] || ` ${userIdFilter}` || t("sharedAll") || "All users"}
          </div>
        )}
        {from && (
          <div>
            <strong>{t("reportFrom") || "From"}:</strong> {new Date(from).toLocaleString()}
          </div>
        )}
        {to && (
          <div>
            <strong>{t("reportTo") || "To"}:</strong> {new Date(to).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  )
}

// Attributes renderer component
const AttributesRenderer = ({ attributes, type }) => {
  const t = useTranslation()

  if (!attributes || !type) return <span>-</span>

  switch (type) {
    case "sendCommand":
      return attributes.command ? <span>{t(prefixString("command", attributes.command))}</span> : <span>-</span>
    default:
      return <span>-</span>
  }
}

const CommandsReportPage = () => {
  const classes = useReportStyles()
  const t = useTranslation()
  const dispatch = useDispatch()

  // State
  const users = useSelector((state) => state?.users?.items || {})
  const devices = useSelector((state) => state?.devices?.items || {})
  const printing = useSelector((state) => state?.session?.printing || false)
  const usersMap = useSelector((state) => state?.usersMap || {})

  const [columns, setColumns] = usePersistedState("commandColumns", ["userId", "eventTime", "deviceId", "attributes"])
  const [eventTypes, setEventTypes] = useState(["sendCommand"])
  const [items, setItems] = useState([])
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [userIdFilter, setUserIdFilter] = useState("")
  const [periodoFilter, setPeriodoFilter] = useState({ from: "", to: "" })
  const [usuarios, setUsuarios] = useState([])
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")

  // Load users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true)
        const response = await fetch("/api/users")
        if (response.ok) {
          const data = await response.json()

          const userNamesMap = {}
          data.forEach((user) => {
            userNamesMap[user.id] = user.name
          })

          dispatch({
            type: "usersMap/update",
            payload: userNamesMap,
          })

          setUsuarios(data)
        } else {
          console.error("Failed to fetch users:", await response.text())
        }
      } catch (error) {
        console.error("Error fetching users:", error)
      } finally {
        setUsersLoading(false)
      }
    }

    fetchUsers()
  }, [dispatch])

  // Handle print event
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener("afterprint", handleAfterPrint)
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [dispatch])

  const fetchCommands = async (filters = {}) => {
    setLoading(true)
    try {
      const query = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          if (Array.isArray(value)) {
            value.forEach((v) => query.append(key, v))
          } else {
            query.append(key, value)
          }
        }
      })

      eventTypes.forEach((type) => query.append("type", type))

      const endpoint = `/api/reports/commands?${query.toString()}`
      const response = await fetch(endpoint)

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType === "application/json") {
          const data = await response.json()
          setAllItems(data)

          // Apply local filters
          const filteredData = applyLocalFilters(data, userIdFilter, periodoFilter)
          setItems(filteredData)
          setPage(0)
        } else {
          window.location.assign(window.URL.createObjectURL(await response.blob()))
        }
      } else {
        throw Error(await response.text())
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      dispatch(errorsActions.push(error.message))
    } finally {
      setLoading(false)
    }
  }

  const applyLocalFilters = (data, userId, period) => {
    let result = [...data]

    if (userId) {
      result = result.filter((item) => item.userId && item.userId.toString() === userId)
    }

    if (period.from || period.to) {
      result = result.filter((item) => {
        if (!item.eventTime) return true

        const itemDate = new Date(item.eventTime).getTime()

        if (period.from && period.to) {
          const fromDate = new Date(period.from).getTime()
          const toDate = new Date(period.to).setHours(23, 59, 59, 999)
          return itemDate >= fromDate && itemDate <= toDate
        } else if (period.from) {
          const fromDate = new Date(period.from).getTime()
          return itemDate >= fromDate
        } else if (period.to) {
          const toDate = new Date(period.to).setHours(23, 59, 59, 999)
          return itemDate <= toDate
        }

        return true
      })
    }

    return result
  }

  const filterByUserId = (userId) => {
    setUserIdFilter(userId)
    const filteredData = applyLocalFilters(allItems, userId, periodoFilter)
    setItems(filteredData)
    setPage(0)
  }

  const filterByPeriod = (from, to) => {
    const newPeriod = { from, to }
    setPeriodoFilter(newPeriod)
    const filteredData = applyLocalFilters(allItems, userIdFilter, newPeriod)
    setItems(filteredData)
    setPage(0)
  }

  const processPresetPeriod = (period) => {
    const today = new Date()
    const startDate = new Date()
    const endDate = new Date()

    switch (period) {
      case "today":
        startDate.setHours(0, 0, 0, 0)
        return { from: startDate.toISOString(), to: endDate.toISOString() }
      case "yesterday":
        startDate.setDate(startDate.getDate() - 1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setDate(endDate.getDate() - 1)
        endDate.setHours(23, 59, 59, 999)
        return { from: startDate.toISOString(), to: endDate.toISOString() }
      case "thisWeek":
        const currentDay = today.getDay()
        const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1
        startDate.setDate(startDate.getDate() - daysToSubtract)
        startDate.setHours(0, 0, 0, 0)
        return { from: startDate.toISOString(), to: endDate.toISOString() }
      case "lastWeek":
        const day = today.getDay()
        const daysToLastWeekStart = day === 0 ? 13 : day + 6
        startDate.setDate(startDate.getDate() - daysToLastWeekStart)
        startDate.setHours(0, 0, 0, 0)
        endDate.setDate(endDate.getDate() - (day === 0 ? 7 : day))
        endDate.setHours(23, 59, 59, 999)
        return { from: startDate.toISOString(), to: endDate.toISOString() }
      case "thisMonth":
        startDate.setDate(1)
        startDate.setHours(0, 0, 0, 0)
        return { from: startDate.toISOString(), to: endDate.toISOString() }
      case "lastMonth":
        startDate.setMonth(startDate.getMonth() - 1)
        startDate.setDate(1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setDate(0)
        endDate.setHours(23, 59, 59, 999)
        return { from: startDate.toISOString(), to: endDate.toISOString() }
      default:
        return { from: "", to: "" }
    }
  }

  const handleSubmit = useCatch(async ({ userIds, from, to, type, period, ...otherFilters }) => {
    if (type === "mail") {
      const response = await fetch(`/api/reports/commands/mail`)
      if (!response.ok) {
        throw Error(await response.text())
      }
    } else {
      let startDate = from
      let endDate = to

      if (period && period !== "custom") {
        const processedPeriod = processPresetPeriod(period)
        startDate = processedPeriod.from
        endDate = processedPeriod.to
      }

      filterByPeriod(startDate, endDate)

      const filters = { ...otherFilters }
      if (startDate) filters.from = startDate
      if (endDate) filters.to = endDate
      if (userIds) filters.userIds = userIds

      await fetchCommands(filters)

      if (type === "export") {
        const table = document.querySelector("table")
        requestAnimationFrame(() => writeFileXLSX(utils.table_to_book(table), "commands.xlsx"))
      } else if (type === "pdf") {
        setFrom(startDate)
        setTo(endDate)
        dispatch(sessionActions.setPrinting(true))
      }
    }
  })

  const handleSchedule = useCatch(async (userIds, groupIds, report) => {
    report.type = "commands"
    // Navigate to scheduled reports page if needed
  })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatValue = (item, key) => {
    if (!item) return ""

    switch (key) {
      case "deviceId":
        return item[key] && devices[item[key]] ? devices[item[key]].name : ""
      case "userId":
        return item[key] && users[item[key]] ? users[item[key]].name : "-"
      case "eventTime":
        return item[key] ? formatTime(item[key]) : ""
      case "attributes":
        if (printing) {
          if (!item.attributes || !item.type) return "-"
          switch (item.type) {
            case "sendCommand":
              return item.attributes.command ? t(prefixString("command", item.attributes.command)) : "-"
            default:
              return "-"
          }
        }
        return <AttributesRenderer attributes={item.attributes} type={item.type} />
      default:
        return item[key] !== undefined ? item[key] : "-"
    }
  }

  if (printing) {
    return (
      <div>
        <PrintHeader from={from} to={to} userIdFilter={userIdFilter} />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id || Math.random()}>
                  {columns.map((column) => (
                    <TableCell key={`${item.id || Math.random()}-${column}`}>{formatValue(item, column)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["reportTitle", "reportCommands"]}>
      <div className={classes.container}>
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              includeUsers
              ignoreDevice
              handleSubmit={handleSubmit}
              handleSchedule={handleSchedule}
              showOnly={undefined}
              multiDevice={undefined}
              includeGroups={undefined}
              loading={loading}
              includeExportOptions={true}
            >
              <Grid item xs={12} sm={6}>
                {usersLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <UserFilter onChange={filterByUserId} users={usuarios} loading={usersLoading} />
                )}
              </Grid>
              <Grid item xs={12} sm={12}>
                <ColumnSelect
                  columns={columns}
                  setColumns={setColumns}
                  columnsArray={columnsArray}
                  rawValues={true}
                  disabled={loading}
                />
              </Grid>
            </ReportFilter>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                items.length > 0 ? (
                  items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                    <TableRow key={item.id || Math.random()} hover>
                      {columns.map((key) => (
                        <TableCell key={`${item.id || Math.random()}-${key}`}>{formatValue(item, key)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow style={{ height: 72 * 5 }}>
                    <TableCell align="center" colSpan={columns.length}>
                      {t("sharedNoData") || "No data available"}
                    </TableCell>
                  </TableRow>
                )
              ) : (
                <TableShimmer columns={columns.length} startAction={false} endAction={false} />
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("sharedRowsPerPage") || "Rows per page:"}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("sharedOf") || "of"} ${count}`}
          />
        </div>
      </div>
    </PageLayout>
  )
}

export default CommandsReportPage